import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  InfoCard,
  Link,
  Progress,
  EmptyState,
} from '@backstage/core-components';
import { Box, Chip, Grid, makeStyles } from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';

const useStyles = makeStyles(theme => ({
  apiList: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  apiRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
  typeChip: {
    height: 22,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.info.dark,
    fontWeight: 600,
  },
  chipLink: {
    textDecoration: 'none',
  },
}));

const getApiType = (entity: Entity) => {
  const type = (entity as any).spec?.type;
  return typeof type === 'string' ? type.toUpperCase() : 'API';
};

export const EntityApisCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const [providedApis, setProvidedApis] = React.useState<Entity[]>([]);
  const [consumedApis, setConsumedApis] = React.useState<Entity[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchApis = async () => {
      const providesApiRefs = (entity as any).spec?.providesApis || [];
      const consumesApiRefs = (entity as any).spec?.consumesApis || [];
      const allApiRefs = [...providesApiRefs, ...consumesApiRefs];

      if (allApiRefs.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const { items } = await catalogApi.getEntities({
          filter: {
            kind: 'API',
          },
        });

        const matchApiRef = (api: Entity, ref: string) => {
          const apiName = api.metadata.name;
          const namespace = api.metadata.namespace || 'default';
          return (
            ref === apiName ||
            ref === `${namespace}/${apiName}` ||
            ref.includes(apiName)
          );
        };

        const provided = items.filter(api =>
          providesApiRefs.some((ref: string) => matchApiRef(api, ref)),
        );

        const consumed = items.filter(api =>
          consumesApiRefs.some((ref: string) => matchApiRef(api, ref)),
        );

        setProvidedApis(provided);
        setConsumedApis(consumed);
      } catch (error) {
        console.error('Error fetching APIs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApis();
  }, [entity, catalogApi]);

  const renderApiReference = (api: Entity, color: 'primary' | 'secondary') => {
    return (
      <Box key={api.metadata.name} className={classes.apiRow}>
        <Link
          to={`/catalog/${api.metadata.namespace || 'default'}/api/${
            api.metadata.name
          }/definition`}
          className={classes.chipLink}
        >
          <Chip
            label={api.metadata.name}
            color={color}
            style={{ margin: '4px', cursor: 'pointer' }}
            clickable
          />
        </Link>
      </Box>
    );
  };

  if (loading) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Progress />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <InfoCard title="Provided APIs">
          {providedApis.length > 0 ? (
            <div>
              {providedApis.map(api => renderApiReference(api, 'primary'))}
            </div>
          ) : (
            <p>This component does not provide any APIs.</p>
          )}
        </InfoCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <InfoCard title="Consumed APIs">
          {consumedApis.length > 0 ? (
            <div>
              {consumedApis.map(api => renderApiReference(api, 'secondary'))}
            </div>
          ) : (
            <p>This component does not consume any APIs.</p>
          )}
        </InfoCard>
      </Grid>
    </Grid>
  );
};
